import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Hidden } from '@material-ui/core';
import styled from 'styled-components';

import {
  PageDescriptor,
  AuthContext,
  applySeoMetadata,
  resolveSeoMetadata,
  useStoredState
} from 'framework';
import { Spacer } from 'components';

import { Page } from './page';
import { Header } from './header';

const CalloutButton = styled(Button)`
  border-radius: 20px;
  padding: ${p => p.theme.spacing(1, 2)}
  background-color: #ffffff;
  margin-right: ${p => p.theme.spacing(2)}px;
  white-space: nowrap;
  min-width: 140px;
`;

const CookieBanner = styled(Box)`
  display: flex;
  position: fixed;
  bottom: 0px;
  left: 0px;
  width: 100%;
  background: ${p => p.theme.palette.primary.main};
  align-items: center;
  color: ${p => p.theme.palette.primary.contrastText};
`;

const RootContainer = styled(Box)`
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export function Template({ page }: { page: PageDescriptor }) {
  const { organization } = useParams<{ organization: string }>();
  const history = useHistory();
  const location = useLocation();
  const {
    isInitialized,
    init,
    isSignedIn,
    requestSignin,
    getGroups
  } = useContext(AuthContext);
  const { t } = useTranslation();
  const [authState, setAuthState] = useState<
    'uninitialized' | 'pending' | 'initialized'
  >('uninitialized');

  const [acceptedCookedBanner, setAcceptedCookieBanner] = useStoredState<
    boolean
  >('spothr-cookiebanner', false);

  useEffect(() => {
    applySeoMetadata(
      resolveSeoMetadata(
        location.pathname,
        page.title ? t(page.title) : undefined
      )
    );
  }, [location.pathname, page.title, t]);

  if (page.authorized && !isInitialized()) {
    const initAuth = async () => {
      setAuthState('pending');
      await init(organization);
      setAuthState('initialized');
    };

    if (authState === 'uninitialized') {
      initAuth();
    }

    return <>{t('loading')}</>;
  }

  if (page.authorized && !isSignedIn()) {
    requestSignin();
    return <>{t('loggingIn')}</>;
  }

  const userGroups = isInitialized() ? getGroups() : [];
  const inGroup = page.groups?.length
    ? page.groups?.some(group => userGroups.includes(group))
    : true;

  if (!inGroup) {
    history.replace(`/${organization}`);
    return <></>;
  }

  return (
    <RootContainer>
      {!page.ignoreContainer && <Header />}
      <Page
        key={page.path}
        ignoreContainer={page.ignoreContainer}
        noHeader={page.noHeader}
      >
        <page.screen />
      </Page>
      {!acceptedCookedBanner && (
        <CookieBanner>
          <Box padding={2}>
            <Hidden mdDown>
              <Typography variant="h4">{t('cookieTitle')}</Typography>
            </Hidden>
            <Hidden smDown>
              <Typography variant="body1">{t('cookieDesc')}</Typography>
            </Hidden>
            <Hidden mdUp>
              <Typography variant="caption">{t('cookieDesc')}</Typography>
            </Hidden>
          </Box>
          <Spacer />
          <CalloutButton
            variant="contained"
            onClick={() => setAcceptedCookieBanner(true)}
          >
            {t('cookieButton')}
          </CalloutButton>
        </CookieBanner>
      )}
    </RootContainer>
  );
}
